import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { getValidAuthHeadersOrThrow } from '../lib/auth';
import { useAuth } from '../contexts/AuthContext';
import { getAdminId } from '../utils/getAdminId';
import type { IssueEmailSchedule, WorkerContact } from '../types/orderIssues';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

export function useIssueEmailSchedule() {
  const { user } = useAuth();
  const adminId = getAdminId(user);

  const [schedule, setSchedule] = useState<IssueEmailSchedule | null>(null);
  const [contacts, setContacts] = useState<WorkerContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSchedule = useCallback(async () => {
    if (!adminId) return;
    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('issue_email_schedule')
        .select('*')
        .eq('admin_id', adminId)
        .maybeSingle();

      if (err) throw err;
      setSchedule(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load schedule');
    } finally {
      setLoading(false);
    }
  }, [adminId]);

  const loadContacts = useCallback(async () => {
    if (!adminId) return;
    const { data } = await supabase
      .from('worker_contacts')
      .select('*')
      .eq('admin_id', adminId)
      .order('name');
    if (data) setContacts(data);
  }, [adminId]);

  const saveSchedule = useCallback(
    async (updates: {
      is_enabled: boolean;
      schedule_days: string[];
      execution_time: string;
      recipient_ids: string[];
    }) => {
      if (!adminId) return;
      setSaving(true);
      setError(null);

      try {
        if (schedule) {
          const { error: err } = await supabase
            .from('issue_email_schedule')
            .update({
              is_enabled: updates.is_enabled,
              schedule_days: updates.schedule_days,
              execution_time: updates.execution_time,
              recipient_ids: updates.recipient_ids,
            })
            .eq('admin_id', adminId);
          if (err) throw err;
        } else {
          const { error: err } = await supabase
            .from('issue_email_schedule')
            .insert({
              admin_id: adminId,
              is_enabled: updates.is_enabled,
              schedule_days: updates.schedule_days,
              execution_time: updates.execution_time,
              recipient_ids: updates.recipient_ids,
            });
          if (err) throw err;
        }
        await loadSchedule();
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to save schedule';
        setError(msg);
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [adminId, schedule, loadSchedule]
  );

  const sendTestEmail = useCallback(
    async (recipientIds: string[]) => {
      if (!adminId || recipientIds.length === 0) return;
      setSendingTest(true);
      setError(null);

      try {
        const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-issue-report-email`;
        const res = await fetch(url, {
          method: 'POST',
          headers: await getValidAuthHeadersOrThrow(),
          body: JSON.stringify({
            test: true,
            admin_id: adminId,
            recipient_ids: recipientIds,
          }),
        });

        const result = await res.json();
        if (!result.success) {
          throw new Error(result.message || 'Test email failed');
        }
        return result;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to send test email';
        setError(msg);
        throw err;
      } finally {
        setSendingTest(false);
      }
    },
    [adminId]
  );

  const sendManualEmail = useCallback(
    async (issueIds: string[], recipientIds: string[]) => {
      if (!adminId || issueIds.length === 0 || recipientIds.length === 0) return;

      try {
        const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-issue-report-email`;
        const res = await fetch(url, {
          method: 'POST',
          headers: await getValidAuthHeadersOrThrow(),
          body: JSON.stringify({
            manual: true,
            admin_id: adminId,
            issue_ids: issueIds,
            recipient_ids: recipientIds,
          }),
        });

        const result = await res.json();
        if (!result.success) {
          throw new Error(result.message || 'Manual email failed');
        }
        return result;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to send email';
        setError(msg);
        throw err;
      }
    },
    [adminId]
  );

  useEffect(() => {
    loadSchedule();
    loadContacts();
  }, [loadSchedule, loadContacts]);

  return {
    schedule,
    contacts,
    loading,
    saving,
    sendingTest,
    error,
    saveSchedule,
    sendTestEmail,
    sendManualEmail,
    loadSchedule,
    loadContacts,
    DAYS,
  };
}
